/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import cx from 'classnames';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector, useStore} from 'react-redux';
import {useHistory} from 'react-router-dom';
import urls from 'urls';
import {navigateToRoute} from '~/CreateFabricInvitation/navigation';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectEditingMobile} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {useBackground} from '~/common/FabricInvitation/EventCreateDesign/components/Background/actions/useBackground';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {useValidation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useValidation';
import {isInCreateFlowNavABTest} from '~/common/fabric/experiments';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {isFabricIOSWebview, isFabricUniversalWebview} from '~/common/utils/webview';
import {useEventSave} from '~/OneStepCreateInvitation/hooks/useEventSave';
import {usePremiumFeatureBlocker} from '~/OneStepCreateInvitation/hooks/usePremiumFeatureBlocker';
import {selectEventId} from '~/OneStepCreateInvitation/reducers/event/selector';
import {Button} from '~library/atoms/Button';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import './BottomButton.sass';

export const BottomButton = ({hasCtaBeenSeen, text, previousRoute}) => {
  const mounted = useRef(false);

  const store = useStore();
  const history = useHistory();

  const isLargishAndUp = useIsLargishAndUp();

  const confirmationType = useSelector(({details}) => details?.confirmationType);
  const eventId = useSelector(selectEventId);
  const {validate, ErrorModal} = useValidation();
  const {onSave, isLoading} = useEventSave();
  const editingMobile = useSelector(selectEditingMobile);
  const isInEditMode = useSelector(eventInEditMode);

  const {isUsingPremiumFeature, shouldShowModal, dismissModal, confirmUpgrade} =
    usePremiumFeatureBlocker();
  const {canvas} = useContext(EventCreateContext);
  const {onTextureChange, selectedBackground} = useBackground(!isLargishAndUp);

  const [height, setHeight] = useState(0);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (confirmationType === 'sent' || confirmationType === 'updated') {
      window.location.href = urls.get('event_dashboard', {event_id: eventId});
    }
  }, [confirmationType]);

  const onFinishHandler = useCallback(() => {
    const hasError = validate?.();
    if (!hasError) {
      setShowLoadingOverlay(true);
      canvasRemoveTextBordersAndIcons(canvas);
      navigateToRoute('next', store).finally(() => {
        if (mounted.current) {
          setShowLoadingOverlay(false);
        }
      });
    }
    return hasError;
  }, [canvas, store, validate]);

  const onBackHandler = useCallback(() => {
    const save = () => {
      setShowLoadingOverlay(true);
      canvasRemoveTextBordersAndIcons(canvas);
      return onSave().finally(() => {
        history.push({
          pathname: `/${eventId}/${previousRoute}`,
          hash: history.location.hash,
          search: history.location.search,
        });
        if (mounted.current) {
          setShowLoadingOverlay(false);
        }
      });
    };

    return save();
  }, [canvas, eventId, history, onSave, previousRoute]);

  const ref = useRef();

  if (ref?.current?.clientHeight && height !== ref?.current?.clientHeight) {
    setHeight(ref.current.clientHeight);
  }

  const confirmUpsell = useCallback(() => {
    confirmUpgrade();
    if (isUsingPremiumFeature) {
      onTextureChange(selectedBackground?.icon);
    }
  }, [confirmUpgrade, isUsingPremiumFeature, selectedBackground, onTextureChange]);

  if (editingMobile && !isLargishAndUp) {
    return null;
  }
  const isInNavABTest = isInCreateFlowNavABTest();
  if (isInNavABTest && !editingMobile) return null;

  return (
    <>
      <div
        className={cx({
          'card-design__nav-bottom__cta_wrapper': true,
          'grayed-out': isUsingPremiumFeature,
          'nav-bottom__webview':
            (isFabricUniversalWebview() && evite.detects.isIOS) || isFabricIOSWebview(),
        })}
        ref={ref}
        style={{
          position: hasCtaBeenSeen ? 'fixed' : 'block',
          bottom: 0,
        }}
      >
        {!isInEditMode && (
          <Button
            className="creation-flow__preview__back-btn"
            data-qa-id="nav-bottom-previous"
            variant="secondary"
            size="large"
            type="button"
            ariaLabel="Back"
            onClick={onBackHandler}
            disabled={isLoading}
          >
            <ArrowIcon direction="left" className="create-flow__form-nav-back-arrow" ratio={0.75} />
          </Button>
        )}
        <Button
          id="navBottom"
          className="creation-flow__preview__next-btn"
          data-qa-id="nav-bottom-next"
          variant="primary"
          size="large"
          onClick={onFinishHandler}
          disabled={isLoading}
          title={text}
        >
          {text}
        </Button>
      </div>
      {hasCtaBeenSeen && <div style={{height}} />}
      {ErrorModal}
      {shouldShowModal ? (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={dismissModal}
          closeOverlay={dismissModal}
          onConfirmAction={confirmUpsell}
          openTray="customize"
          mode="unlock-design-tools"
          cancelCtaText="Dismiss"
          hideSubtitle
        />
      ) : null}
      {createPortal(
        <LoadingOverlay loadingMessage="Saving..." show={showLoadingOverlay} />,
        document.getElementById('pbModalRoot')
      )}
    </>
  );
};
